import React, {useCallback} from 'react';

import {BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {GrantResourcesDetails} from 'src/components/grant-resources';
import {RowNumbered} from 'src/components/row-numbered';
import {
    getDischargeAccountEntitlementsProgress,
    getDischargeAccountEntitlementsResult,
} from 'src/openapi/admin/apis/BulkDischargeAccountEntitlementsApi';
import {useAppDispatch} from 'src/store/hooks';

import {Dropdown} from '@pb/kit-component-dropdown';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {entitlementsResultSortComparator} from '../utils/entitlements-result-sort-comparator';

import {TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UpdateAccountRelationResult} from 'src/openapi/admin/models/UpdateAccountRelationResult';


type Props = {
    id: string,
    onClose: () => void,
};

export const UnassignAccountEntitlementsResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: UpdateAccountRelationResult[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UpdateAccountRelationResult, index) => (
                <Row key={(currentPage - 1) * pageSize + index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        {!!res.roleName && (
                            <EllipsisTextHint title={res.roleName}>
                                {res.roleName}
                            </EllipsisTextHint>
                        )}
                    </Cell>
                    <Cell>
                        {res && res.grantResources && !res.roleName ? (
                            <Dropdown
                                hoverEnabled={true}
                                anchor={({anchorRef, getAnchorProps}) => (
                                    <span ref={anchorRef} {...getAnchorProps()}>
                                        <div className="ellipsis">
                                            {res.grantResources?.resources?.map(({name}) => name).join(', ')}
                                        </div>
                                    </span>
                                )}>
                                <GrantResourcesDetails
                                    grantId={res.grantResources.grantId}
                                    totalCount={res.grantResources.totalCount}
                                />
                            </Dropdown>
                        ) : null}
                    </Cell>
                    <Cell>
                        {!!res.grantName && (
                            <EllipsisTextHint title={res.grantName}>
                                {res.grantName}
                            </EllipsisTextHint>
                        )}
                    </Cell>
                    <Cell><CompleteMark errors={res.errors}/></Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<UpdateAccountRelationResult>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={() => (
                <Group justify="space-between">
                    <b>Unassigned resources</b>
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}><strong>#</strong></Cell>
                        <Cell width={150}>Role</Cell>
                        <Cell width={350}>Resources</Cell>
                        <Cell>Grant Name</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getDischargeAccountEntitlementsProgress({id}))}
            getResult={() => apiDispatch(getDischargeAccountEntitlementsResult({id}))}

            sortComparator={entitlementsResultSortComparator}
        />
    );
};
